import React, { useCallback, memo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { updateProduction } from 'services/api/production';

import ColoredTagSelection from 'components/Tags/ColoredTagSelection';
import { notify } from 'helpers/notificationHelper';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';

const ProductionTags = ({
    production,
    tagLabel = 'Etiketler',
    tagGroupID,
    multiple = true,
    dbKey = 'tags',
    open = false,
    onClose = null,
}) => {
    const [hasError, setHasError] = useState(false);
    const selectedTags = production?.[dbKey] || [];

    const handleChanges = useCallback(
        (key, value) => {
            if (!production?._id) {
                notify(false, 'Üretim ID bulunamadı');
                return;
            }

            const tag = {};
            const productionID = production._id;
            tag[key] = Array.isArray(value) ? value.map((el) => el._id) : value?._id;

            updateProduction(productionID, tag)
                .then((rp) => {
                    notify(rp.data.success, rp.data.message);
                    setHasError(false);
                    // Close dialog after successful update if in dialog mode
                    if (onClose) {
                        onClose();
                    }
                })
                .catch((err) => {
                    console.error('Tag update error:', err);
                    notify(false, 'Etiket güncellenirken hata oluştu');
                    setHasError(true);
                });
        },
        [production, onClose]
    );

    // Don't render if no production or no tagGroupID
    if (!production || !tagGroupID) {
        return null;
    }

    // Show simple fallback if there's an error
    if (hasError) {
        const errorContent = (
            <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => setHasError(false)}
                    sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
                >
                    <Icon sx={{ fontSize: '14px', mr: 0.5 }}>refresh</Icon>
                    Etiketleri Yenile
                </MDButton>
            </MDBox>
        );

        if (open && onClose) {
            return (
                <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="h6">Etiket Hatası</MDTypography>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </MDBox>
                    </DialogTitle>
                    <DialogContent>{errorContent}</DialogContent>
                </Dialog>
            );
        }

        return errorContent;
    }

    const tagContent = (
        <ColoredTagSelection
            tagGroupID={tagGroupID}
            selectedTags={selectedTags}
            tagLabel={tagLabel}
            multiple={multiple}
            handleChanges={handleChanges}
            dbKey={dbKey}
        />
    );

    // If dialog mode is requested
    if (open && onClose) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6">{tagLabel}</MDTypography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </MDBox>
                </DialogTitle>
                <DialogContent>
                    <MDBox py={1}>{tagContent}</MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} variant="outlined" color="secondary">
                        Kapat
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }

    // Normal inline mode
    return <MDBox>{tagContent}</MDBox>;
};

export default memo(ProductionTags);
